// src/features/calendar/hooks/useCalendarHandlers.ts
// 日曆事件處理器：導航、排程、拖拽
// 從 Calendar.tsx 提取

import { useState, useCallback, useMemo } from 'react';
import { ScheduleItem } from '../../../types';
import { Task } from '../../../types/task';
import { isEvent, isTimeBox } from '../../../utils/calendarHelper';
import { RescheduleRequest } from './useCalendarDragDrop';
import { updateTimeBox, addTaskToTimeBox } from '../../../services/api/timeBoxes';
import { eventService } from '../../../services/api/calendar-events';
import { logger } from '../../../utils/logger';
import { format } from 'date-fns';
import type { CalendarView } from '../types';
import {
  buildAndSaveLocalTimeBox,
  addTimeBoxIdToTask,
  syncTimeBoxToFirestore,
} from '../utils/timeBoxCreation';
import { taskService } from '../../task';
import { getTimeBoxById, getTaskById, revertTaskLocal } from '../../../services/indexedDB';
import { eventBus, EVENTS } from '../../../utils/eventBus';

interface CalendarModals {
  timeBoxDetail: { open: (id: string) => void };
  eventDetail: { open: (id: string) => void };
  quickCreateModal: { open: (start: Date, end: Date) => void };
}

interface UseCalendarHandlersOptions {
  view: CalendarView;
  date: Date;
  setDate: (date: Date) => void;
  scheduleItems: ScheduleItem[];
  modals: CalendarModals;
  refreshData: () => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message: string) => void;
}

export function useCalendarHandlers({
  view,
  date,
  setDate,
  scheduleItems,
  modals,
  refreshData,
  showSuccess,
  showError,
}: UseCalendarHandlersOptions) {

  // 樂觀更新狀態：儲存拖拽時的臨時時間變更
  const [optimisticUpdates, setOptimisticUpdates] = useState<Map<string, { startTime: Date; endTime: Date }>>(new Map());

  // 合併樂觀更新到實際數據
  const displayScheduleItems = useMemo(() => {
    if (optimisticUpdates.size === 0) return scheduleItems;

    return scheduleItems.map(item => {
      const key = `${item.itemType}-${item.id}`;
      const optimisticData = optimisticUpdates.get(key);
      if (optimisticData) {
        return {
          ...item,
          startTime: optimisticData.startTime,
          endTime: optimisticData.endTime,
        };
      }
      return item;
    });
  }, [scheduleItems, optimisticUpdates]);

  // 點擊日程項目 - 處理 TimeBox 和 Event
  const handleSelectScheduleItem = useCallback((item: ScheduleItem) => {
    if (isTimeBox(item)) {
      modals.timeBoxDetail.open(item.id);
    } else if (isEvent(item)) {
      modals.eventDetail.open(item.id);
    }
  }, [modals.timeBoxDetail, modals.eventDetail]);

  // 點擊時段 - 打開 QuickCreateModal
  const handleSelectSlot = useCallback((start: Date, end: Date) => {
    modals.quickCreateModal.open(start, end);
  }, [modals.quickCreateModal]);

  // 拖拽重新排程（含樂觀更新）
  const handleReschedule = useCallback(async (request: RescheduleRequest) => {
    const key = `${request.itemType}-${request.itemId}`;

    // 樂觀更新：立即更新 UI
    setOptimisticUpdates(prev => new Map(prev).set(key, {
      startTime: request.newStartTime,
      endTime: request.newEndTime,
    }));

    try {
      if (request.itemType === 'timebox') {
        await updateTimeBox({
          id: request.itemId,
          startTime: request.newStartTime,
          endTime: request.newEndTime,
        });
        logger.dev('TimeBox time updated:', request.itemId);
      } else if (request.itemType === 'event') {
        await eventService.updateEvent(request.itemId, {
          id: request.itemId,
          startTime: request.newStartTime,
          endTime: request.newEndTime,
        });
        logger.dev('Event time updated:', request.itemId);
      }

      // 成功：清除樂觀更新（讓即時數據接管）
      setOptimisticUpdates(prev => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      refreshData();
    } catch (error) {
      logger.error('Reschedule failed:', error);

      // 失敗：回滾樂觀更新
      setOptimisticUpdates(prev => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      showError('排程更新失敗', '無法更新時間，請檢查網路連線後重試');
    }
  }, [refreshData, showError]);

  // 從 Task 創建 TimeBox（桌面版拖曳放置）
  const handleCreateTimeBoxFromTask = useCallback(async (
    task: Task,
    startTime: Date,
    endTime: Date,
  ) => {
    try {
      // 1. 建立本地 TimeBox（寫入 IndexedDB）
      const localTimeBox = await buildAndSaveLocalTimeBox(task, startTime, endTime);

      // 2. 更新 Task 的 scheduledTimeBoxIds + lastTimeboxedDate（IndexedDB）
      const lastTimeboxedDate = await addTimeBoxIdToTask(task.id, localTimeBox.id, format(startTime, 'yyyy-MM-dd'));

      // 3. 觸發 UI 更新（此時 IndexedDB 資料已正確，task 會即時消失）
      eventBus.publish(EVENTS.TIMEBOX.CREATED, localTimeBox);
      refreshData();

      // 4. 背景同步到 Firestore
      await syncTimeBoxToFirestore(localTimeBox, task, lastTimeboxedDate);
    } catch (error) {
      logger.error('Failed to create TimeBox from task:', error);
      showError('Schedule Failed', 'Failed to create time box from task.');
    }
  }, [refreshData, showError]);

  // 將 Task 添加到既有 TimeBox
  const handleAddTaskToTimeBox = useCallback(async (
    timeBoxId: string,
    taskId: string,
  ) => {
    // 在寫入 IndexedDB 前記錄快照，供 Firestore 失敗時 rollback
    const taskSnapshot = await getTaskById(taskId).catch((err) => {
      logger.warn('Calendar', 'Failed to read task snapshot before addTaskToTimeBox', err);
      return null;
    });

    try {
      // 1. 取得 TimeBox 的 startTime 以計算 lastTimeboxedDate
      const timeBox = await getTimeBoxById(timeBoxId);
      if (!timeBox) {
        logger.warn('Calendar', 'TimeBox not found in IndexedDB, falling back to today', { timeBoxId });
      }
      const timeBoxDate = timeBox ? format(timeBox.startTime, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

      // 2. 更新 Task 的 scheduledTimeBoxIds + lastTimeboxedDate（IndexedDB）
      const lastTimeboxedDate = await addTimeBoxIdToTask(taskId, timeBoxId, timeBoxDate);

      // 3. 觸發 UI 更新（此時 IndexedDB 資料已正確，task 會即時消失）
      showSuccess('Task added to Timebox');
      refreshData();

      // 4. 同步 Firestore（addTaskToTimeBox 的 transaction 已更新雙方的 scheduledTimeBoxIds）
      await Promise.all([
        addTaskToTimeBox(timeBoxId, taskId),
        lastTimeboxedDate ? taskService.updateLastTimeboxedDate(taskId, lastTimeboxedDate) : Promise.resolve(),
      ]);
    } catch (error) {
      logger.error('Calendar', 'Failed to add task to TimeBox', error);

      if (taskSnapshot) {
        try {
          await revertTaskLocal(taskId, taskSnapshot);
          refreshData();
        } catch (rollbackError) {
          logger.error('Calendar', 'Rollback failed after addTaskToTimeBox error', rollbackError);
        }
      }

      showError('Add Task Failed', 'Failed to add task to time box.');
    }
  }, [refreshData, showSuccess, showError]);

  // 行動版快速排程：點擊任務直接創建 TimeBox
  const handleQuickScheduleTask = useCallback(async (task: Task) => {
    const now = new Date();
    const startTime = new Date(now);
    startTime.setMinutes(0, 0, 0);
    startTime.setHours(now.getHours() + 1);

    const durationMinutes = task.estimatedTime || 60;
    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

    try {
      // 1. 建立本地 TimeBox（寫入 IndexedDB）
      const localTimeBox = await buildAndSaveLocalTimeBox(task, startTime, endTime);

      // 2. 更新 Task 的 scheduledTimeBoxIds + lastTimeboxedDate（IndexedDB）
      const lastTimeboxedDate = await addTimeBoxIdToTask(task.id, localTimeBox.id, format(startTime, 'yyyy-MM-dd'));

      // 3. 觸發 UI 更新 + 立即打開 modal（IndexedDB 已有 temp ID 的 TimeBox 資料）
      eventBus.publish(EVENTS.TIMEBOX.CREATED, localTimeBox);
      refreshData();
      modals.timeBoxDetail.open(localTimeBox.id);

      // 4. 背景同步到 Firestore（syncTimeBoxToFirestore 會在 IndexedDB 中將 temp ID 替換為真實 ID）
      await syncTimeBoxToFirestore(localTimeBox, task, lastTimeboxedDate);
    } catch (error) {
      logger.error('Calendar', 'Quick schedule task failed', error);
      showError('Schedule Failed', 'Failed to schedule task.');
    }
  }, [refreshData, showError, modals.timeBoxDetail]);

  // 導航：上一個時間段
  const handlePrev = useCallback(() => {
    const newDate = new Date(date);
    if (view === 'day') newDate.setDate(newDate.getDate() - 1);
    else if (view === 'threeDays') newDate.setDate(newDate.getDate() - 3);
    else if (view === 'week') newDate.setDate(newDate.getDate() - 7);
    else if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
    setDate(newDate);
  }, [date, view, setDate]);

  // 導航：下一個時間段
  const handleNext = useCallback(() => {
    const newDate = new Date(date);
    if (view === 'day') newDate.setDate(newDate.getDate() + 1);
    else if (view === 'threeDays') newDate.setDate(newDate.getDate() + 3);
    else if (view === 'week') newDate.setDate(newDate.getDate() + 7);
    else if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
    setDate(newDate);
  }, [date, view, setDate]);

  // 導航：今天
  const handleToday = useCallback(() => {
    const today = new Date();
    if (view === 'threeDays') {
      // ThreeDayView: 讓今天顯示在中間
      const adjustedDate = new Date(today);
      adjustedDate.setDate(adjustedDate.getDate() - 1);
      setDate(adjustedDate);
    } else {
      setDate(today);
    }
  }, [view, setDate]);

  // 處理日期選擇
  const handleDateSelect = useCallback((selectedDate: Date) => {
    if (view === 'threeDays') {
      // 3-day view: 讓選中日在中間
      const adjustedDate = new Date(selectedDate);
      adjustedDate.setDate(adjustedDate.getDate() - 1);
      setDate(adjustedDate);
    } else {
      setDate(selectedDate);
    }
  }, [view, setDate]);

  return {
    displayScheduleItems,
    handleSelectScheduleItem,
    handleSelectSlot,
    handleReschedule,
    handleCreateTimeBoxFromTask,
    handleAddTaskToTimeBox,
    handleQuickScheduleTask,
    handlePrev,
    handleNext,
    handleToday,
    handleDateSelect,
  };
}
